/**
 * preview-selection-slice.ts — Phase 048 Plan 02 Task 1 (D-048-06).
 *
 * Standalone Zustand store carrying the Preview-only selection state — the
 * Select/Interact mode flag, the current `(blockId, propertyKey)` selection
 * ref, and the transient hovered element id. Read by `usePreviewSelection`
 * (the capture-phase DOM-delegation listener), `PreviewSelectionOverlay` (the
 * ring + label affordance), and `PreviewSelectionToggle` (the segmented
 * Select/Interact button group).
 *
 * Why a STANDALONE `create()` store (NOT a slice composed into
 * `src/store/studio-store.ts`):
 *   - The studio store runs through the `persist` middleware with a
 *     `UI_PERSIST_KEYS` allow-list; selection state is transient editor
 *     state that must NEVER persist to `localStorage` (Phase 049 will
 *     overwrite it on every project switch). The closest analog in the
 *     codebase is `preview-testing-store.ts` — a standalone `create()` store
 *     under `features/preview/store/` with no middleware; this file matches
 *     its shape byte-for-byte. (RESEARCH Pattern 3 — Section 3.)
 *   - The selection layer is Preview-only (D-048-06 — "a new, lightweight,
 *     Preview-only selection layer"); isolating it in a feature-local store
 *     keeps the shared studio store's slice composition unchanged.
 *
 * Defaults:
 *   - `previewMode` initial value is `'interact'` — D-048-02 ("Editable
 *     Preview defaults to Interact mode on load"). The first click in
 *     Preview must behave exactly like v2.7; the operator opts into Select
 *     via the toggle.
 *
 * `PreviewSelectionRef` is re-exported from `@/features/page-editor/store/types`
 * so consumers can `import { PreviewSelectionRef } from
 * '@/features/preview/store/preview-selection-slice'` without a separate
 * page-editor import — the type was defined on `LoadedApp.aboxElementMap` by
 * Plan 01 (Task 1) and lives there to avoid a `page-editor` ↔ `preview`
 * import-direction edge.
 */

import { create } from 'zustand'
import type { PreviewSelectionRef } from '@/features/page-editor/store/types'

export type { PreviewSelectionRef }

/** Select/Interact toggle state — D-048-01 / D-048-02. */
export type PreviewMode = 'interact' | 'select'

export interface PreviewSelectionState {
  /**
   * Whether clicks in the Preview canvas drive the selection layer (`'select'`)
   * or pass through to the running app (`'interact'`). Default `'interact'`
   * (D-048-02); flipping to `'select'` is the operator's opt-in act.
   */
  previewMode: PreviewMode
  /**
   * The currently-selected `(blockId, propertyKey)` ref, or `null` when
   * nothing is selected. A Select-mode click on a non-property element (pure
   * wrapper / synthesized Add chrome / empty canvas) sets this to `null`
   * (D-048-05 — "deselect").
   */
  previewSelection: PreviewSelectionRef | null
  /**
   * The currently-hovered element id (the renderer's `data-cogo-element-id`
   * value), or `null` when nothing is hovered. Drives the hover affordance
   * only — never used for `(blockId, propertyKey)` resolution downstream.
   */
  previewHoverElementId: string | null

  setPreviewMode: (m: PreviewMode) => void
  setPreviewSelection: (s: PreviewSelectionRef | null) => void
  setPreviewHover: (id: string | null) => void
  reset: () => void
}

const initialState = {
  previewMode: 'interact' as PreviewMode,
  previewSelection: null as PreviewSelectionRef | null,
  previewHoverElementId: null as string | null,
}

export const usePreviewSelectionStore = create<PreviewSelectionState>()(
  (set, _get) => ({
    ...initialState,

    setPreviewMode: (previewMode) => set({ previewMode }),

    setPreviewSelection: (previewSelection) => set({ previewSelection }),

    setPreviewHover: (previewHoverElementId) => set({ previewHoverElementId }),

    reset: () => set({ ...initialState }),
  }),
)
